import { ErrorBoundary, LoadingState } from '@ibe/ui-web';
import { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { NotFoundPage } from './not-found-page.js';
import { SiteLayout } from '../shared/layout/site-layout.js';

const HomePage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/home-page.js')).HomePage,
}));
const SearchPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/search-page.js')).SearchPage,
}));
const PropertyDetailsPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/property-details-page.js'))
    .PropertyDetailsPage,
}));
const AvailabilityPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/availability-page.js')).AvailabilityPage,
}));
const PricingPage = lazy(async () => ({
  default: (await import('../features/booking/pages/pricing-page.js')).PricingPage,
}));
const GuestDetailsPage = lazy(async () => ({
  default: (await import('../features/booking/pages/guest-details-page.js')).GuestDetailsPage,
}));
const PaymentPage = lazy(async () => ({
  default: (await import('../features/booking/pages/payment-page.js')).PaymentPage,
}));
const ConfirmationPage = lazy(async () => ({
  default: (await import('../features/booking/pages/confirmation-page.js')).ConfirmationPage,
}));
const BookingLookupPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/booking-lookup-page.js'))
    .BookingLookupPage,
}));
const BookingDetailsPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/booking-details-page.js'))
    .BookingDetailsPage,
}));
const CancellationPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/cancellation-page.js'))
    .CancellationPage,
}));
const ReservationDetailPage = lazy(async () => ({
  default: (await import('../features/booking/pages/reservation-detail-page.js'))
    .ReservationDetailPage,
}));

export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route
          path="/"
          element={
            <Boundary>
              <HomePage />
            </Boundary>
          }
        />
        <Route
          path="/search"
          element={
            <Boundary>
              <SearchPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId"
          element={
            <Boundary>
              <PropertyDetailsPage />
            </Boundary>
          }
        />
        <Route
          path="/availability"
          element={
            <Boundary>
              <AvailabilityPage />
            </Boundary>
          }
        />
        <Route
          path="/pricing"
          element={
            <Boundary>
              <PricingPage />
            </Boundary>
          }
        />
        <Route
          path="/guest-details"
          element={
            <Boundary>
              <GuestDetailsPage />
            </Boundary>
          }
        />
        <Route
          path="/payment"
          element={
            <Boundary>
              <PaymentPage />
            </Boundary>
          }
        />
        <Route
          path="/confirmation/:bookingReference"
          element={
            <Boundary>
              <ConfirmationPage />
            </Boundary>
          }
        />
        <Route
          path="/reservation/:bookingReference"
          element={
            <Boundary>
              <ReservationDetailPage />
            </Boundary>
          }
        />
        <Route
          path="/lookup"
          element={
            <Boundary>
              <BookingLookupPage />
            </Boundary>
          }
        />
        <Route
          path="/booking/:bookingReference"
          element={
            <Boundary>
              <BookingDetailsPage />
            </Boundary>
          }
        />
        <Route
          path="/cancel/:bookingReference"
          element={
            <Boundary>
              <CancellationPage />
            </Boundary>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function Boundary({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Loading" />}>{children}</Suspense>
    </ErrorBoundary>
  );
}
